// @ts-nocheck

import { createSelector } from 'reselect';

import { BigBoostedPresetsValue, BoostedPresetsValue } from '../../constants/presets';
import Formatter from '../../i18n/formatter';
import { __ } from '../../i18n/l10n';
import { getItemIdFromProps, getOddsFromProps } from '../../selectors/_propsGetters_';
import { selectBasketIsCashout, selectBasketMode } from '../../selectors/basket/basketPrimary.selector';
import {
  selectBasketIsInputDisabled,
  selectBasketShowResume,
  makeBasketActiveStakeSelector,
  makeBasketIsFreebetSelector,
  makeBasketItemIdSelector,
  makeBasketItemIsValidatedSelector,
} from '../../selectors/basketCommon.selector';
import { selectBasketEnhancedItems, selectBasketEnhancedItem } from '../../selectors/basketItems.selector';
import { selectBetBoostedOddCardMode } from '../../selectors/bet.selector';
import { selectEntitiesMatches } from '../../selectors/entities/matches/entitiesMatchesPrimary.selector';
import {
  selectUserBalance,
  selectUserFreebetBankroll,
  selectUserPresets,
} from '../../selectors/user/userPrimary.selector';
import { type StateType } from '../../states/index';
import { BasketModes } from '../../types/basket';
import { CardModes } from '../../types/common';
import { SportIds } from '../../types/sports';
import { getPresetsFromOdds } from '../../utils/presets';
import { createShallowEqualSelector } from '../../utils/reselect';

export const makeBasketPresetPropsSelector = () => {
  const selectActiveStake = makeBasketActiveStakeSelector();

  const getIsVisible = createSelector(
    [
      selectBasketMode,
      selectBasketIsCashout,
      selectBasketEnhancedItems,
      selectBasketShowResume,
      getItemIdFromProps,
      makeBasketItemIsValidatedSelector(),
    ],
    (mode, isCashout, items, showResume, itemIdModeSimple, isItemIsValidated) => {
      const isModeSimple = mode === BasketModes.SIMPLE;
      const isMultipleSimpleBets = isModeSimple && items.length > 1;
      const isOneSimpleBet = isModeSimple && items.length === 1;
      return (
        !isItemIsValidated &&
        !isCashout &&
        !showResume &&
        ((isOneSimpleBet && !itemIdModeSimple) ||
          (!isModeSimple && !itemIdModeSimple) ||
          (isMultipleSimpleBets && itemIdModeSimple))
      );
    },
  );

  const getBalance = createSelector(
    [selectUserBalance, selectUserFreebetBankroll, makeBasketIsFreebetSelector()],
    (userBalance, freebetBalance, isFreebet) => {
      return isFreebet ? freebetBalance : userBalance;
    },
  );

  const getItemMatch = createSelector(
    [selectBasketMode, selectBasketEnhancedItem, selectBasketEnhancedItems, selectEntitiesMatches],
    (mode, item, items, matches) => {
      const isOneSimpleBet = mode === BasketModes.SIMPLE && items.length === 1;

      if (isOneSimpleBet) {
        return matches[items[0]?.matchId];
      }

      return matches[item?.matchId];
    },
  );

  const getBoostedOddCardMode = (
    state: StateType,
    props?: {
      itemId?: string | number;
      oddId?: string | number;
      oddsId?: string | number;
    },
  ) => {
    const match = getItemMatch(state, props);

    if (match && match.sportId === SportIds.SPECIAL_ODDS) {
      return selectBetBoostedOddCardMode(state, { betId: match.mainBetId });
    }

    return CardModes.NORMAL;
  };

  const getSpecificPresets = createShallowEqualSelector(
    [selectUserPresets, getOddsFromProps, getBalance, selectActiveStake, getIsVisible, getBoostedOddCardMode],
    (presetsValues, odds, balance, stake, isVisible, cardMode) => {
      let presets = [];
      if (isVisible) {
        let tmpPresets = [];
        if (cardMode === CardModes.BOOSTED) {
          tmpPresets = BoostedPresetsValue;
        } else if (cardMode === CardModes.BIG_BOOSTED) {
          tmpPresets = BigBoostedPresetsValue;
        } else {
          tmpPresets = getPresetsFromOdds(presetsValues, odds);
        }
        presets = tmpPresets.map<{ label: string; value: number | null }>((p) => ({
          label: p === -1 ? __('ALL-IN') : Formatter.formatNumber(p / 100, '€', true, true, true),
          value: p !== -1 || balance ? (p === -1 ? balance / 100 : p / 100) : null,
          highlight: p !== -1 ? p / 100 === parseFloat(stake) : balance / 100 === parseFloat(stake),
        }));
      }
      return presets;
    },
  );

  return createShallowEqualSelector(
    [selectBasketIsInputDisabled, selectActiveStake, makeBasketItemIdSelector(), getSpecificPresets],
    (isDisabled, stake, itemId, presets) => {
      return {
        itemId,
        presets,
        stake,
        isDisabled,
      };
    },
  );
};
